import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  CircularProgress,
  Link
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';

interface MFAVerificationProps {
  tempToken: string;
  mfaType: string;
  username: string;
  onSuccess: (token: string) => void;
  onCancel: () => void;
}

export const MFAVerification: React.FC<MFAVerificationProps> = ({
  tempToken,
  mfaType,
  username,
  onSuccess,
  onCancel
}) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingBackup, setUsingBackup] = useState(false);
  const [resendingOTP, setResendingOTP] = useState(false);

  const handleVerify = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/login/verify-mfa`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            temp_token: tempToken,
            code: code.replace(/\D/g, ''),
            mfa_type: usingBackup ? 'backup' : mfaType
          })
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Invalid verification code');
      }

      const data = await response.json();
      onSuccess(data.access_token);
    } catch (err: any) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setResendingOTP(true);
    setError(null);

    try {
      // This would need the user ID, which we don't have here
      // In production, the backend should handle resend via temp_token
      setError('Please contact support to resend OTP');
    } catch (err: any) {
      setError(err.message || 'Failed to resend OTP');
    } finally {
      setResendingOTP(false);
    }
  };

  const getMFATypeLabel = () => {
    if (usingBackup) return 'Backup Code';
    if (mfaType === 'totp') return 'Authenticator App';
    if (mfaType === 'email') return 'Email OTP';
    return 'Verification Code';
  };

  const getPlaceholder = () => {
    if (usingBackup) return 'XXXX-XXXX';
    return '000000';
  };

  const handleCodeChange = (value: string) => {
    if (usingBackup) {
      // Allow letters, numbers, and dash for backup codes
      setCode(value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 9));
    } else {
      // Only numbers for OTP/TOTP
      setCode(value.replace(/\D/g, '').slice(0, 6));
    }
  };

  return (
    <Paper sx={{ p: 4, maxWidth: 400, mx: 'auto', mt: 8 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
        <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center' }}>
          Verification Required
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
          {username}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!usingBackup && mfaType === 'email' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          We've sent a verification code to your email address.
        </Alert>
      )}

      {!usingBackup && mfaType === 'totp' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Enter the 6-digit code from your authenticator app.
        </Alert>
      )}

      {usingBackup && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Enter one of your backup recovery codes.
        </Alert>
      )}

      <TextField
        fullWidth
        label={getMFATypeLabel()}
        value={code}
        onChange={(e) => handleCodeChange(e.target.value)}
        placeholder={getPlaceholder()}
        autoFocus
        sx={{ mb: 2 }}
        inputProps={{
          style: {
            fontSize: '1.5rem',
            textAlign: 'center',
            letterSpacing: '0.5rem',
            fontFamily: 'monospace'
          }
        }}
      />

      <Button
        fullWidth
        variant="contained"
        onClick={handleVerify}
        disabled={loading || code.length < (usingBackup ? 8 : 6)}
        sx={{ mb: 2 }}
      >
        {loading ? <CircularProgress size={24} /> : 'Verify'}
      </Button>

      <Box sx={{ textAlign: 'center' }}>
        {!usingBackup ? (
          <>
            {mfaType === 'email' && (
              <Link
                component="button"
                variant="body2"
                onClick={handleResendOTP}
                disabled={resendingOTP}
                sx={{ mb: 1, display: 'block' }}
              >
                {resendingOTP ? 'Sending...' : 'Resend Code'}
              </Link>
            )}
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                setUsingBackup(true);
                setCode('');
                setError(null);
              }}
              sx={{ mb: 1, display: 'block' }}
            >
              Use Backup Code
            </Link>
          </>
        ) : (
          <Link
            component="button"
            variant="body2"
            onClick={() => {
              setUsingBackup(false);
              setCode('');
              setError(null);
            }}
            sx={{ mb: 1, display: 'block' }}
          >
            Use {mfaType === 'totp' ? 'Authenticator App' : 'Email Code'}
          </Link>
        )}
        <Link
          component="button"
          variant="body2"
          onClick={onCancel}
          sx={{ display: 'block' }}
        >
          Cancel Login
        </Link>
      </Box>
    </Paper>
  );
};
